import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { FormsModule }                  from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { NgbModule }                    from '@ng-bootstrap/ng-bootstrap';
import { BrowserModule }                from '@angular/platform-browser';
import { BrowserAnimationsModule }      from '@angular/platform-browser/animations';
import { ToastrModule }                 from 'ngx-toastr';
import { AppInit }                      from './app.init';
import { UserValidationError }          from './app.init';
import { Routing }                      from './app.routing';
import { AppSettings }                  from './app.settings';
import { AppComponent }                 from './app.component';
import { httpInterceptorProviders }     from './interceptors/interceptors';

import { ServiceModule }                from './services/service.module';
import { BlockUIModule }                from './block-ui';
import { ThemeModule }                  from './theme/theme.module';
import { PipesModule }                  from './pipes/pipes.module';
import { PublicApiComponent }           from './components/public-api.component';
import { NotFoundComponent }            from './components/not-found/not-found.component';
import { NotSupportedComponent }        from './components/not-supported/not-supported.component';
import { NgSelectModule }               from '@ng-select/ng-select';

import { StoreModule } from '@ngrx/store';

/**
 * The app's HttpClient wiring, exported so a spec can assert on the exact same
 * array the app uses.
 *
 * withXhr() keeps the pre-v22 XHR backend. Angular 22 made FetchBackend the
 * default, which would have silently changed the network layer under every
 * service — different error shapes, different cancellation. The
 * `http-xhr-backend` migration does NOT catch this call site, because it only
 * fires where HttpXhrBackend is referenced explicitly.
 *
 * Guarded by app-http.spec.ts. Moving to fetch is a separate task with its own QA.
 */
export const APP_HTTP_PROVIDERS = [
    provideHttpClient(withInterceptorsFromDi(), withXhr()),
];
import { reducers } from './store/app.store';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import arraySupport from 'dayjs/plugin/arraySupport';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// import { registerLocaleData } from '@angular/common';
// import localeCa from '@angular/common/locales/en-CA';
// import localeUS from '@angular/common/locales/en';
// import localeDe from '@angular/common/locales/de';
// import localeColumbia from '@angular/common/locales/es-CO';
// registerLocaleData(localeCa);
// registerLocaleData(localeUS);
// registerLocaleData(localeDe);
// registerLocaleData(localeColumbia);

export function initApp(appInit: AppInit) {
    return async () => {
        try {
            await appInit.init();
        } catch (e) {
            if (e instanceof UserValidationError) {
                window.location.href = '/login';
            } else {
                console.error('Failed to initialize app', e);
                window.document.body.classList.add('failed');
                window.location.href = '/assets/html/error.html';
            }
            const forever = new Promise(() => undefined); // will never resolve
            await forever;
        }
    }
};


@NgModule({
    declarations: [
        AppComponent,
        PublicApiComponent,
        NotFoundComponent,
        NotSupportedComponent,
        // SearchResultsComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        ServiceModule.forRoot(),
        BlockUIModule.forRoot(),
        ThemeModule,
        PipesModule,
        NgbModule,
        ToastrModule.forRoot({
            preventDuplicates: true,
        }),
        Routing,
        NgSelectModule,
        FormsModule,
        StoreModule.forRoot(reducers),
    ],
    exports: [
        ThemeModule,
        PipesModule,
    ],
    providers: [
        APP_HTTP_PROVIDERS,
        httpInterceptorProviders,
        AppSettings,
        AppInit,
        provideAppInitializer(() => initApp(inject(AppInit))()),
    ],
    bootstrap: [ AppComponent ]
})

export class        AppModule { 
    constructor(){
        dayjs.extend(utc);
        dayjs.extend(arraySupport)
        dayjs.extend(customParseFormat)
    }
}
